/** Editing and signing off the mission summary's situation, and its version history.
 *
 *  The situation can be edited and signed off once the mission has ended (`summary.editable`): each save is the
 *  summary's next version, marked as the editor's (sentio missionagent/integration/editing). Signing off signs
 *  the version saved, or the one shown when the text is unchanged; an edit after a sign-off is the next version,
 *  unsigned until signed off in turn. From then on the AI drafts the summary no more, and the ePCR download
 *  carries the edited or signed-off summary as an addendum.
 *
 *  The history shows every version as two branches: the AI's drafts, and the edits made on this page, each
 *  edit joined to the version it was made from (the one edited, or the one restored). Selecting a version
 *  shows its situation and what that was written from: the crew messages and device events the AI cited. */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { styled, useTheme } from "@mui/material/styles";
import { useCookies } from "react-cookie";

import {
  errorMessage,
  fetchSummaryVersions,
  restoreSummaryVersion,
  saveSummarySituation,
  timeZoneLabel,
} from "../api";
import { ramp } from "../theme";
import type { MissionSummaryCard, SummaryVersion } from "../types";
import { formatAt } from "./MissionHeader";
import { STILL_DRAFTING, SignedOffChip, signerName, titleSizedButtonSx } from "./SignOff";

/** As the API's limit (editing/summary.py MAX_SITUATION_LENGTH). */
const MAX_SITUATION_LENGTH = 4000;

/** The two branches. Steel blue and coral, the brand's two hues (validated as a pair: CVD ΔE 18). Identity is
 *  never colour alone: drafts are circles, edits squares, and every row says which it is. */
const LANE_COLOR = { ai: ramp.primary[500], manual: ramp.accent[600] } as const;
const LANE_LABEL = { ai: "AI draft", manual: "Manual edit" } as const;

function useToken(): string | undefined {
  const [cookies] = useCookies(["access_token"]);
  return cookies.access_token;
}

type EditProps = {
  missionId: string;
  summary: MissionSummaryCard;
  /** Called once saved: the page re-reads the mission. */
  onChanged: () => void;
};

/** "Edit & Sign Off": the situation in a text box, saved as the summary's next version, and/or signed off. */
export function SummaryEditButton({
  missionId,
  summary,
  drafting,
  onChanged,
}: EditProps & { /** The AI has a newer summary on its way: wait for it. */ drafting: boolean }) {
  const token = useToken();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [signOff, setSignOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setText(summary.situation ?? "");
    setSignOff(false);
    setError(null);
    setOpen(true);
  };
  const trimmed = text.trim();
  const unchanged = trimmed === (summary.situation ?? "").trim();
  // The version shown is signed off already: signing it again needs a change (a new version).
  const shownSigned = summary.signed_off?.version === summary.version;
  const canSign = !(unchanged && shownSigned);
  const action = signOff ? (unchanged ? "Sign off" : "Save & sign off") : "Save";

  const save = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await saveSummarySituation(token, missionId, summary.version, trimmed, signOff && canSign);
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={drafting ? STILL_DRAFTING : "Edit the situation, sign it off, or both"}>
        <span>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditOutlinedIcon />}
            disabled={drafting}
            onClick={start}
            sx={titleSizedButtonSx}
          >
            Edit & Sign Off
          </Button>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit & sign off the situation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            A change is saved as version {summary.version + 1}, marked as yours. Signing off signs that version, or
            version {summary.version} as shown when you leave the text unchanged, and logs you and the time. The AI
            stops drafting the summary from then on, and the ePCR carries it as an addendum. You can still edit it
            after signing off: the edit is a new version, not signed off. Earlier versions and sign-offs stay in the
            history.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={14}
            label="Situation"
            value={text}
            onChange={(event) => setText(event.target.value)}
            inputProps={{ maxLength: MAX_SITUATION_LENGTH }}
            helperText={`${text.length} / ${MAX_SITUATION_LENGTH}`}
          />
          <Tooltip title={canSign ? "" : `Version ${summary.version} is signed off already: change it to sign off again`}>
            <FormControlLabel
              control={
                <Switch checked={signOff && canSign} disabled={!canSign} onChange={(e) => setSignOff(e.target.checked)} />
              }
              label="Sign off"
              sx={{ mt: 0.5, "& .MuiFormControlLabel-label": { textTransform: "uppercase", fontWeight: 600 } }}
            />
          </Tooltip>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !trimmed || (unchanged && !(signOff && canSign))}
            onClick={() => void save()}
          >
            {action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const ROW_HEIGHT = 44;
const LANE_X = { ai: 18, manual: 62 } as const;
/** Edges between the branches run down this gutter, clear of both branches' nodes; edges whose rows overlap
 *  take separate tracks in it, so two edits made from different drafts never share a line. */
const GUTTER_X = 40;
const TRACK_STEP = 6;
const MAX_TRACKS = 4;
/** An edge that skips nodes of its own branch bulges out past it. */
const OUTER_X = { ai: 4, manual: 76 } as const;
const GRAPH_WIDTH = 84;
const NODE = 5;

/** The branch graph: one row per version, newest at the top. */
function edgePath(child: Placed, parent: Placed, between: boolean, track = 0): string {
  const { x: cx, y: cy } = child;
  const { x: px, y: py } = parent;
  if (child.lane !== parent.lane) {
    // Out of the child sideways, down its track of the gutter, into the parent sideways.
    const gx = GUTTER_X + (track % 2 ? 1 : -1) * Math.ceil(track / 2) * TRACK_STEP;
    return `M ${cx} ${cy} C ${gx} ${cy}, ${gx} ${cy}, ${gx} ${cy + ROW_HEIGHT / 2} L ${gx} ${
      py - ROW_HEIGHT / 2
    } C ${gx} ${py}, ${gx} ${py}, ${px} ${py}`;
  }
  if (!between) return `M ${cx} ${cy} L ${px} ${py}`;
  const outer = OUTER_X[child.lane];
  return `M ${cx} ${cy} C ${outer} ${cy}, ${outer} ${cy}, ${outer} ${cy + ROW_HEIGHT / 2} L ${outer} ${
    py - ROW_HEIGHT / 2
  } C ${outer} ${py}, ${outer} ${py}, ${px} ${py}`;
}

type Placed = { version: SummaryVersion; lane: "ai" | "manual"; x: number; y: number; row: number };

function BranchGraph({ rows }: { rows: Placed[] }) {
  const byVersion = new Map(rows.map((p) => [p.version.version, p]));
  // Gutter tracks, first free by row span (rows run newest first, so a child's row is above its parent's).
  const taken: Array<Array<[number, number]>> = [];
  const edges = rows.flatMap((child) => {
    const parent = child.version.parent_version === null ? undefined : byVersion.get(child.version.parent_version);
    if (!parent) return [];
    const between = rows.some((p) => p.lane === child.lane && p.row > child.row && p.row < parent.row);
    let track = 0;
    if (child.lane !== parent.lane) {
      const span: [number, number] = [child.row, parent.row];
      track = taken.findIndex((spans) => spans.every(([a, b]) => span[1] <= a || span[0] >= b));
      if (track === -1) track = Math.min(taken.length, MAX_TRACKS - 1);
      (taken[track] ??= []).push(span);
    }
    return [{ child, parent, d: edgePath(child, parent, between, track) }];
  });
  return (
    <svg
      width={GRAPH_WIDTH}
      height={rows.length * ROW_HEIGHT}
      style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
      aria-hidden
    >
      {edges.map(({ child, parent, d }) => (
        <path
          key={`${child.version.version}-${parent.version.version}`}
          d={d}
          fill="none"
          stroke={LANE_COLOR[child.lane]}
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray={child.lane !== parent.lane ? "4 3" : undefined}
        />
      ))}
      {rows.map(({ version: v, lane, x, y }) => {
        const color = LANE_COLOR[lane];
        const hollow = v.review_status === "superseded";
        const size = v.shown ? NODE + 1.5 : NODE;
        const fill = hollow ? "#FFFFFF" : color;
        return (
          <g key={v.version}>
            {v.shown ? (
              lane === "manual" ? (
                <rect x={x - size - 3} y={y - size - 3} width={(size + 3) * 2} height={(size + 3) * 2} rx={3}
                  fill="none" stroke={color} strokeWidth={1.5} />
              ) : (
                <circle cx={x} cy={y} r={size + 3} fill="none" stroke={color} strokeWidth={1.5} />
              )
            ) : null}
            {lane === "manual" ? (
              <rect x={x - size} y={y - size} width={size * 2} height={size * 2} rx={2} fill={fill} stroke={color}
                strokeWidth={2} />
            ) : (
              <circle cx={x} cy={y} r={size} fill={fill} stroke={color} strokeWidth={2} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

const Legend = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5, 2),
  padding: theme.spacing(1, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const LegendItem = ({ shape, color, label }: { shape: "circle" | "square" | "hollow"; color: string; label: string }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    <svg width={12} height={12} aria-hidden>
      {shape === "square" ? (
        <rect x={1} y={1} width={10} height={10} rx={2} fill={color} />
      ) : (
        <circle cx={6} cy={6} r={4.5} fill={shape === "hollow" ? "#FFFFFF" : color} stroke={color} strokeWidth={1.5} />
      )}
    </svg>
    {label}
  </Box>
);

const Row = styled(ButtonBase, { shouldForwardProp: (prop) => prop !== "selected" })<{ selected: boolean }>(
  ({ theme, selected }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    height: ROW_HEIGHT,
    paddingLeft: GRAPH_WIDTH + 4,
    paddingRight: theme.spacing(1.5),
    gap: theme.spacing(1),
    textAlign: "left",
    background: selected ? theme.palette.action.selected : "transparent",
    "&:hover": { background: selected ? theme.palette.action.selected : theme.palette.action.hover },
    "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  }),
);

const Detail = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  minWidth: 0,
  overflowY: "auto",
}));

/** The sources, as the drafts' own source lists read (DraftPanel). */
const SourceList = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75, 1.25),
  borderLeft: `2px solid ${LANE_COLOR.ai}`,
  background: theme.palette.background.default,
  ...theme.typography.body2,
  "& > div + div": { marginTop: theme.spacing(0.75) },
}));

const at = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

function Sources({ version }: { version: SummaryVersion }) {
  if (version.sources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {version.source === "manual"
          ? `${version.author ?? "The editor"}'s own statement: not taken from recorded messages or events.`
          : "No recorded message or event is cited for it."}
      </Typography>
    );
  }
  return (
    <>
      {version.sources_from ? (
        <Typography variant="body2" color="text.secondary">
          Restored unchanged from v{version.sources_from}; these are the sources the AI cited for it.
        </Typography>
      ) : null}
      <SourceList>
        {version.sources.map((source) => (
          <div key={source.event_id}>
            <Typography component="span" variant="body2" color="text.secondary">
              {source.kind} · {at(source.at)} {timeZoneLabel(new Date(source.at))}
              {source.text ? " — " : ""}
            </Typography>
            {source.text ? `“${source.text}”` : null}
          </div>
        ))}
      </SourceList>
    </>
  );
}

const Block = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
}));

type HistoryProps = EditProps;

/** "History": the versions as two branches; select one to read it, and restore it once the mission has ended. */
export function SummaryHistoryButton({ missionId, summary, onChanged }: HistoryProps) {
  const token = useToken();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<SummaryVersion[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      const rows = await fetchSummaryVersions(token, missionId);
      setVersions(rows);
      setSelected((current) =>
        current !== null && rows.some((v) => v.version === current)
          ? current
          : (rows.find((v) => v.shown) ?? rows[rows.length - 1])?.version ?? null,
      );
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [token, missionId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const placed: Placed[] = useMemo(
    () =>
      [...(versions ?? [])].reverse().map((version, row) => ({
        version,
        lane: version.source,
        x: LANE_X[version.source],
        y: row * ROW_HEIGHT + ROW_HEIGHT / 2,
        row,
      })),
    [versions],
  );
  const chosen = versions?.find((v) => v.version === selected) ?? null;
  const shown = versions?.find((v) => v.shown) ?? null;

  const restore = async (version: SummaryVersion) => {
    if (!token || !shown) return;
    setBusy(true);
    setError(null);
    try {
      await restoreSummaryVersion(token, missionId, shown.version, version.version);
      setSelected(null); // the reload selects the new version, now shown
      await load();
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)} sx={titleSizedButtonSx}>
        History
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth fullScreen={narrow}>
        <DialogTitle>Mission summary versions</DialogTitle>
        {/* A fixed height: each pane scrolls on its own rather than the dialog growing with the longer one. */}
        <DialogContent
          dividers
          sx={{
            p: 0,
            display: "flex",
            flexDirection: narrow ? "column" : "row",
            height: narrow ? undefined : "min(70vh, 720px)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: narrow ? "0 0 260px" : "0 0 340px",
              minHeight: 0,
              borderRight: narrow ? 0 : 1,
              borderBottom: narrow ? 1 : 0,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Legend>
              <LegendItem shape="circle" color={LANE_COLOR.ai} label={LANE_LABEL.ai} />
              <LegendItem shape="square" color={LANE_COLOR.manual} label={LANE_LABEL.manual} />
              <LegendItem shape="hollow" color={LANE_COLOR.ai} label="Superseded draft" />
            </Legend>
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {versions === null && !error ? (
                <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : null}
              {versions && versions.length === 0 ? (
                <Typography sx={{ p: 2 }} color="text.secondary">
                  No versions yet.
                </Typography>
              ) : null}
              {placed.length ? (
                <Box sx={{ position: "relative" }} role="listbox" aria-label="Summary versions, newest first">
                  <BranchGraph rows={placed} />
                  {placed.map(({ version: v }) => (
                    <Row
                      key={v.version}
                      selected={v.version === selected}
                      onClick={() => setSelected(v.version)}
                      role="option"
                      aria-selected={v.version === selected}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: v.shown ? 600 : 400 }}>
                          v{v.version} · {LANE_LABEL[v.source]}
                          {v.source === "manual" && v.author ? ` · ${v.author}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {formatAt(v.created_at)}
                          {v.review_status === "superseded" ? " · superseded" : ""}
                          {v.signed_off ? ` · signed off by ${signerName(v.signed_off)}` : ""}
                        </Typography>
                      </Box>
                      {v.signed_off ? <Chip size="small" color="success" variant="outlined" label="Signed off" /> : null}
                      {v.shown ? <Chip size="small" variant="outlined" label="Shown" /> : null}
                    </Row>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
          <Detail sx={{ flex: 1, minHeight: 0 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {chosen ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="subtitle1">Version {chosen.version}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={LANE_LABEL[chosen.source]}
                    sx={{ borderColor: LANE_COLOR[chosen.source] }}
                  />
                  {chosen.shown ? <Chip size="small" label="Shown on the page" /> : null}
                  {chosen.signed_off ? <SignedOffChip signed={chosen.signed_off} /> : null}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {chosen.source === "manual"
                    ? `Edited by ${chosen.author ?? "unknown"} · ${formatAt(chosen.created_at)}`
                    : `Drafted by the AI${chosen.author ? ` (${chosen.author})` : ""} · ${formatAt(chosen.created_at)}`}
                  {chosen.parent_version !== null && chosen.source === "manual"
                    ? ` · made from v${chosen.parent_version}`
                    : ""}
                  {chosen.review_status === "superseded" ? " · superseded: newer facts arrived while it was drafted" : ""}
                </Typography>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Situation
                  </Typography>
                  <Block>{chosen.situation ?? "No situation in this version."}</Block>
                </Box>
                {chosen.situation ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography variant="overline" color="text.secondary">
                      Written from
                    </Typography>
                    <Sources version={chosen} />
                  </Box>
                ) : null}
              </>
            ) : versions && versions.length ? (
              <Typography color="text.secondary">Select a version to read it.</Typography>
            ) : null}
          </Detail>
        </DialogContent>
        <DialogActions>
          {summary.editable && chosen && !chosen.shown && chosen.situation ? (
            <Button variant="outlined" disabled={busy} onClick={() => void restore(chosen)}>
              Restore v{chosen.version}&apos;s situation
            </Button>
          ) : null}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
